import React from 'react';
import { Form, Input, Button, Checkbox, Typography, message } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined } from '@ant-design/icons';
import { CognitoUserAttribute } from "amazon-cognito-identity-js";
import { useNavigate } from 'react-router-dom';
import UserPool from "../../utils/userPool";
import useTranslation from "../../hooks/useTranslation";

/**
 * Проверка почты без регулярного выражения: у вложенных квантификаторов время
 * разбора нелинейно, и вставленная в поле строка в мегабайт подвешивала вкладку.
 * Разбор по частям линеен и делает то же самое — «что-то, собака, что-то с точкой».
 */
const isEmailLike = (email) => {
    // Поле необязательное на уровне формы, поэтому сюда приходит и undefined:
    // прежняя регулярка на нём просто возвращала false, и это поведение сохраняем.
    if (typeof email !== 'string') {
        return false;
    }
    const parts = email.split('@');
    if (parts.length !== 2) {
        return false;
    }
    const [local, domain] = parts;
    const noSpaces = (value) => value.length > 0 && !/\s/.test(value);
    return noSpaces(local) && noSpaces(domain) && domain.includes('.') && !domain.startsWith('.')
        && !domain.endsWith('.');
};

const { Title } = Typography;

const RegistrationPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const onFinish = (values) => {
        const { email, password, confirmPassword, givenName, familyName, acceptTerms } = values;

        // Email validation regex
        if (!isEmailLike(email)) {
            message.error(t('auth.invalidEmail'));
            return;
        }

        if (password !== confirmPassword) {
            message.error(t('auth.passwordsMismatch'));
            return;
        }

        if (!acceptTerms) {
            message.error(t('auth.acceptTermsRequired'));
            return;
        }

        UserPool.signUp(email, password, [
            new CognitoUserAttribute({ Name: 'email', Value: email }),
            new CognitoUserAttribute({ Name: 'given_name', Value: givenName }),
            new CognitoUserAttribute({ Name: 'family_name', Value: familyName }),
        ], null, (err) => {
            if (err) {
                message.error(t('auth.registrationFailed', { message: err.message }));
                return;
            }
            message.success(t('auth.registrationSuccess'));
            navigate('/confirm-email', { state: { email } });
        });
    };

    return (
        <div style={{ maxWidth: 360, margin: 'auto', padding: '2rem' }}>
            <Title level={2} style={{ textAlign: 'center' }}>{t('auth.createAccountTitle')}</Title>
            <Form onFinish={onFinish} layout="vertical">
                <Form.Item
                    name="givenName"
                    rules={[{ required: true, message: t('auth.firstNameRequired') }]}
                >
                    <Input
                        prefix={<UserOutlined />}
                        placeholder={t('auth.firstName')}
                    />
                </Form.Item>
                <Form.Item
                    name="familyName"
                    rules={[{ required: true, message: t('auth.lastNameRequired') }]}
                >
                    <Input
                        prefix={<UserOutlined />}
                        placeholder={t('auth.lastName')}
                    />
                </Form.Item>
                <Form.Item
                    name="email"
                    rules={[{ required: true, message: t('auth.emailRequired') }]}
                >
                    <Input
                        prefix={<MailOutlined />}
                        placeholder={t('auth.emailAddress')}
                    />
                </Form.Item>
                <Form.Item
                    name="password"
                    rules={[{ required: true, message: t('auth.passwordRequired') }]}
                >
                    <Input.Password
                        prefix={<LockOutlined />}
                        placeholder={t('auth.password')}
                    />
                </Form.Item>
                <Form.Item
                    name="confirmPassword"
                    rules={[{ required: true, message: t('auth.confirmPasswordRequired') }]}
                >
                    <Input.Password
                        prefix={<LockOutlined />}
                        placeholder={t('auth.confirmPassword')}
                    />
                </Form.Item>
                <Form.Item
                    name="acceptTerms"
                    valuePropName="checked"
                    rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(t('auth.acceptTermsRequired')) }]}
                >
                    <Checkbox>{t('auth.acceptTerms')}</Checkbox>
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit" block>
                        {t('auth.createAccount')}
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default RegistrationPage;
